// Context object + type + hook for DubbingContext, split out of the .tsx so the
// component file only exports the DubbingProvider component
// (react-refresh/only-export-components).

import { createContext, useContext } from 'react';
import type { DubbingState } from './types';
import type { MediaType } from '@/components/common/MediaUploader';
import type { AudioLanguageConfig } from '../common/LanguageSelector';
import type { AudioTrackMode, LLMProvider } from '@/types';
import type { useDubbingState } from './hooks';
import { type CostGateStatus } from '../../utils/costGate';

export interface DubbingContextValue {
  // Full reducer state
  state: DubbingState;
  // Raw action creators from useDubbingState
  actions: ReturnType<typeof useDubbingState>['actions'];
  // Derived status flags
  isUploading: boolean;
  isDubbing: boolean;
  hasResult: boolean;
  showForm: boolean;
  // LLM models (depend on state.llmProvider / state.enableLlmRefinement)
  llmModels: Array<{ label: string; value: string; disabled?: boolean }>;
  llmSelectedModel: string;
  // Whether the cost card lets dubbing start (src/utils/costGate.ts)
  costGate: CostGateStatus;
  setCostGate: (status: CostGateStatus) => void;
  // Token estimate params (null when not enough data)
  /** The operation the cost card must ask about: audio and video dubbing are two routes. */
  estimateOperationPath: string;
  estimateParams: Record<string, unknown> | null;
  // High-level handlers
  handleMediaSelect: (file: File, type: MediaType) => void;
  handleMediaRemove: () => void;
  handleSourceLanguageChange: (language: string) => void;
  handleVoiceConfigsChange: (configs: AudioLanguageConfig[]) => void;
  handleAudioTrackModeChange: (mode: AudioTrackMode) => void;
  handleEnableLlmRefinementChange: (enabled: boolean) => void;
  handleLlmProviderChange: (provider: LLMProvider) => void;
  handleLlmModelChange: (model: string) => void;
  handleStartDubbing: () => Promise<void>;
  handleCancel: () => void;
  handleDownload: () => void;
  handleOpenFolder: () => void;
}

export const DubbingContext = createContext<DubbingContextValue | null>(null);

export function useDubbing(): DubbingContextValue {
  const ctx = useContext(DubbingContext);
  if (ctx === null) {
    throw new Error('useDubbing must be used within a DubbingProvider');
  }
  return ctx;
}
